import { useEffect, useState } from 'react'

import { apiFetch } from './api'
import { Dashboard } from './components/Dashboard'
import { DocumentWorkspace } from './components/DocumentWorkspace'
import { LoginPage } from './components/LoginPage'
import type { AppView, AuthState } from './state'

const documentPath = /^\/documents\/([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\/?$/iu

function viewFromLocation(): AppView {
  const match = window.location.pathname.match(documentPath)
  return match?.[1] ? { name: 'document', documentId: match[1] } : { name: 'dashboard' }
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' })
  const [view, setView] = useState<AppView>(viewFromLocation)

  useEffect(() => {
    let active = true
    apiFetch<{ actor: string }>('/api/auth/session')
      .then(({ actor }) => {
        if (active) setAuth({ status: 'authenticated', actor })
      })
      .catch(() => {
        if (active) setAuth({ status: 'anonymous' })
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const navigateHistory = () => setView(viewFromLocation())
    window.addEventListener('popstate', navigateHistory)
    return () => window.removeEventListener('popstate', navigateHistory)
  }, [])

  function navigate(next: AppView, replace = false) {
    const path = next.name === 'document' ? `/documents/${next.documentId}` : '/'
    window.history[replace ? 'replaceState' : 'pushState'](null, '', path)
    setView(next)
  }

  async function login(actor: string, password: string) {
    const session = await apiFetch<{ actor: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ actor, password }),
    })
    setAuth({ status: 'authenticated', actor: session.actor })
  }

  async function logout() {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' })
    } finally {
      setAuth({ status: 'anonymous' })
      navigate({ name: 'dashboard' }, true)
    }
  }

  if (auth.status === 'loading') {
    return (
      <main className="loading-page" aria-busy="true">
        <div className="loading-mark">SHC</div>
        <p>正在整理你的工作台…</p>
      </main>
    )
  }
  if (auth.status === 'anonymous') return <LoginPage onLogin={login} />

  if (view.name === 'document') return <DocumentWorkspace documentId={view.documentId} onBack={() => navigate({ name: 'dashboard' })} />
  return <Dashboard actor={auth.actor} onOpen={(documentId) => navigate({ name: 'document', documentId })} onLogout={() => void logout()} />
}
